"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckIcon, XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { PopoverContentProps } from "@radix-ui/react-popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// --- Types ---

type HSV = { h: number; s: number; v: number };
type RGB = { r: number; g: number; b: number };

type ColorPickerProps = {
  value?: string;
  onValueChange?: (hex: string) => void;
  swatches?: string[];
  hideContrastRatio?: boolean;
  hideDefaultSwatches?: boolean;
  contrastWith?: string; // New prop
  className?: string;
} & PopoverContentProps;

// --- Math Utilities (Minimal set for Hex <-> HSV) ---

const hexToRgb = (hex: string): RGB => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

const rgbToHex = ({ r, g, b }: RGB): string => {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
      .toUpperCase()
  );
};

const rgbToHsv = ({ r, g, b }: RGB): HSV => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  const v = max;
  let h = 0;

  if (max !== min) {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, v: v * 100 };
};

const hsvToRgb = ({ h, s, v }: HSV): RGB => {
  h /= 360;
  s /= 100;
  v /= 100;
  let r = 0,
    g = 0,
    b = 0;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }
  return { r: r * 255, g: g * 255, b: b * 255 };
};

// --- Interactive Components ---

const SaturationArea = ({
  hsv,
  onChange,
}: {
  hsv: HSV;
  onChange: (hsv: HSV) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return;
      const { left, top, width, height } =
        containerRef.current.getBoundingClientRect();
      const clientX =
        "touches" in event ? event.touches[0].clientX : event.clientX;
      const clientY =
        "touches" in event ? event.touches[0].clientY : event.clientY;

      const x = Math.max(0, Math.min(1, (clientX - left) / width));
      const y = Math.max(0, Math.min(1, (clientY - top) / height));

      onChange({ ...hsv, s: x * 100, v: 100 - y * 100 });
    },
    [hsv, onChange]
  );

  const handleUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleUp);
      window.addEventListener("touchmove", handleMove);
      window.addEventListener("touchend", handleUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, handleMove, handleUp]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full cursor-crosshair rounded-md overflow-hidden select-none"
      style={{
        backgroundColor: `hsl(${hsv.h}, 100%, 50%)`,
        backgroundImage: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)`,
      }}
      onMouseDown={(e) => {
        setIsDragging(true);
        const { left, top, width, height } =
          e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - left) / width));
        const y = Math.max(0, Math.min(1, (e.clientY - top) / height));
        onChange({ ...hsv, s: x * 100, v: 100 - y * 100 });
      }}
      onTouchStart={() => setIsDragging(true)}
    >
      <div
        className="absolute w-3 h-3 border-2 border-white rounded-full shadow-sm -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%` }}
      />
    </div>
  );
};

const HueSlider = ({
  hue,
  onChange,
}: {
  hue: number;
  onChange: (hue: number) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return;
      const { left, width } = containerRef.current.getBoundingClientRect();
      const clientX =
        "touches" in event ? event.touches[0].clientX : event.clientX;
      const x = Math.max(0, Math.min(1, (clientX - left) / width));
      onChange(Math.round(x * 360));
    },
    [onChange]
  );

  const handleUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleUp);
      window.addEventListener("touchmove", handleMove);
      window.addEventListener("touchend", handleUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, handleMove, handleUp]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-3 rounded-md cursor-pointer select-none"
      style={{
        background:
          "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)",
      }}
      onMouseDown={(e) => {
        setIsDragging(true);
        const { left, width } = e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - left) / width));
        onChange(Math.round(x * 360));
      }}
      onTouchStart={() => setIsDragging(true)}
    >
      <div
        className="absolute h-full w-2 bg-white border border-gray-300 rounded shadow-sm -translate-x-1/2 pointer-events-none"
        style={{ left: `${(hue / 360) * 100}%` }}
      />
    </div>
  );
};

// --- Main Component ---

function ColorPicker({
  value = "#000000",
  children,
  swatches = [],
  hideContrastRatio = false,
  hideDefaultSwatches = false,
  contrastWith, // Destructure new prop
  onValueChange,
  className,
  ...props
}: ColorPickerProps) {
  // We keep internal state in HSV because it is easier to calculate the UI positions
  const [internalHsv, setInternalHsv] = useState<HSV>(
    rgbToHsv(hexToRgb(value))
  );

  // Sync when prop value changes
  useEffect(() => {
    setInternalHsv(rgbToHsv(hexToRgb(value)));
  }, [value]);

  const handleColorChange = (newHsv: HSV) => {
    setInternalHsv(newHsv);
    const newHex = rgbToHex(hsvToRgb(newHsv));
    onValueChange?.(newHex);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
      if (val.length === 7) {
        handleColorChange(rgbToHsv(hexToRgb(val)));
      }
    }
  };

  return (
    <Popover {...props}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className={cn("w-[300px] p-0", className)} {...props}>
        <div className="space-y-3 p-3">
          {/* Saturation Area */}
          <div className="w-full aspect-[4/2]">
            <SaturationArea hsv={internalHsv} onChange={handleColorChange} />
          </div>

          {/* Hue Slider */}
          <HueSlider
            hue={internalHsv.h}
            onChange={(h) => handleColorChange({ ...internalHsv, h })}
          />

          {/* Hex Input */}
          <div className="flex gap-2 items-center">
            <div
              className="w-8 h-8 rounded border shadow-sm shrink-0"
              style={{ backgroundColor: rgbToHex(hsvToRgb(internalHsv)) }}
            />
            <Input
              value={rgbToHex(hsvToRgb(internalHsv))}
              onChange={handleHexInputChange}
              className="font-mono uppercase text-center"
              maxLength={7}
            />
          </div>

          {/* Swatches */}
          {(swatches.length > 0 || !hideDefaultSwatches) && <Separator />}
          {!hideDefaultSwatches && (
            <div className="flex flex-wrap gap-1.5">
              {[
                "#000000",
                "#FFFFFF",
                "#F8371A",
                "#F97C1B",
                "#FAC81C",
                "#3FD0B6",
                "#2CADF6",
                "#6462FC",
                ...swatches,
              ].map((color) => (
                <button
                  type="button"
                  key={color}
                  className="size-5 rounded-md border border-transparent hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-black"
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorChange(rgbToHsv(hexToRgb(color)))}
                />
              ))}
            </div>
          )}

          {/* Contrast Ratio */}
          {!hideContrastRatio && (
            <>
              <Separator />
              <ContrastRatio
                hex={rgbToHex(hsvToRgb(internalHsv))}
                contrastWith={contrastWith} // Pass to component
              />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- Contrast Ratio Logic ---

function ContrastRatio({
  hex,
  contrastWith,
}: {
  hex: string;
  contrastWith?: string;
}) {
  const getLuminance = (r: number, g: number, b: number) => {
    const a = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  };

  const getContrast = (l1: number, l2: number) => {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  };

  const BadgeItem = ({
    ratio,
    target,
    text,
  }: {
    ratio: number;
    target: number;
    text: string;
  }) => {
    const pass = ratio >= target;
    return (
      <Badge
        variant="outline"
        className={cn(
          "h-5 px-1.5 rounded-sm font-mono text-[10px]",
          pass
            ? "bg-emerald-500/10 text-emerald-600 border-emerald-200"
            : "bg-red-500/10 text-red-600 border-red-200"
        )}
      >
        {pass ? (
          <CheckIcon size={10} className="mr-1" />
        ) : (
          <XIcon size={10} className="mr-1" />
        )}
        {text}
      </Badge>
    );
  };

  const rgb = hexToRgb(hex);
  const lum = getLuminance(rgb.r, rgb.g, rgb.b);

  // New logic: Compare with specific color
  if (contrastWith) {
    const bgRgb = hexToRgb(contrastWith);
    const bgLum = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
    const ratio = getContrast(lum, bgLum);

    return (
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Legibility Score
        </span>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            Ratio: {ratio.toFixed(2)}
          </span>
          <div className="flex gap-1">
            <BadgeItem ratio={ratio} target={4.5} text="AA" />
            <BadgeItem ratio={ratio} target={7} text="AAA" />
          </div>
        </div>
      </div>
    );
  }

  // Fallback: Existing Logic (vs Black/White)
  const darkModeRatio = (1.0 + 0.05) / (lum + 0.05);
  const lightModeRatio = (lum + 0.05) / 0.05;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-muted-foreground">
        Contrast Ratio
      </span>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          <span className="dark:hidden">
            Light BG: {lightModeRatio.toFixed(2)}
          </span>
          <span className="hidden dark:inline">
            Dark BG: {darkModeRatio.toFixed(2)}
          </span>
        </span>
        <div className="flex gap-1">
          <div className="contents dark:hidden">
            <BadgeItem ratio={lightModeRatio} target={4.5} text="AA" />
            <BadgeItem ratio={lightModeRatio} target={7} text="AAA" />
          </div>
          <div className="hidden dark:contents">
            <BadgeItem ratio={darkModeRatio} target={4.5} text="AA" />
            <BadgeItem ratio={darkModeRatio} target={7} text="AAA" />
          </div>
        </div>
      </div>
    </div>
  );
}

export { ColorPicker };
export type { ColorPickerProps };
